import React, { createContext, useContext, useState, useEffect } from 'react';
import supabase from '../supabase/supabase';

const ThemeContext = createContext();

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

const defaultThemeConfig = {
  hero: {
    title: 'La Vie Saigon',
    subtitle: 'Life, Bread & Tea',
    description: 'Where French elegance meets Vietnamese soul.',
    backgroundImage: 'https://cdn1.sharemyimage.com/smi/2025/10/21/ChatGPT-Image-Oct-20-2025-11_07_27-PM.png',
    primaryButtonText: 'Order Online',
    primaryButtonLink: '/menu',
    secondaryButtonText: 'Visit Us Today',
    secondaryButtonLink: '#contact'
  },
  about: {
    title: 'Our Story',
    paragraphs: [
      'At La Vie Saigon, we blend the timeless charm of Parisian cafés with the vibrant soul of Saigon.',
      'Our space celebrates life — La Vie — through the universal languages of food, coffee, and community.'
    ],
    image: 'https://cdn1.sharemyimage.com/smi/2025/10/27/Kitchen.png'
  },
  menu: {
    title: 'Our Menu',
    subtitle: 'Discover our signature dishes and beverages, crafted with love and authenticity.',
    buttonText: 'View Full Menu',
    buttonLink: '/menu',
    categories: [
      {
        title: 'Bánh Mì',
        subtitle: 'Traditional Vietnamese sandwiches',
        image: 'https://images.unsplash.com/photo-1558030006-450675393462?auto=format&fit=crop&w=800&q=80',
        items: ['Classic Pork', 'Grilled Chicken', 'Vegetarian']
      },
      {
        title: 'Coffee & Tea',
        subtitle: 'Artisan beverages',
        image: 'https://images.unsplash.com/photo-1495474472287-4d71bcdd2085?auto=format&fit=crop&w=800&q=80',
        items: ['Vietnamese Coffee', 'French Press', 'Jasmine Tea']
      },
      {
        title: 'Pastries',
        subtitle: 'Fresh baked daily',
        image: 'https://images.unsplash.com/photo-1578985545062-69928b1d9587?auto=format&fit=crop&w=800&q=80',
        items: ['Croissants', 'Pain au Chocolat', 'Macarons']
      }
    ]
  },
  contact: {
    title: 'Come Visit Us',
    subtitle: 'Experience the warmth of La Vie Saigon in person',
    location: 'Richmond, VA',
    hours: [
      { days: 'Mon–Fri', time: '10:00 AM – 8:00 PM' },
      { days: 'Sat–Sun', time: '10:00 AM – 9:00 PM' }
    ],
    directionsButtonText: 'Get Directions',
    directionsButtonLink: 'https://maps.google.com',
    orderButtonText: 'Order Online',
    orderButtonLink: '/menu',
    image: 'https://cdn1.sharemyimage.com/smi/2025/10/21/ChatGPT-Image-Oct-20-2025-11_07_27-PM.png'
  },
  footer: {
    logo: 'https://cdn1.sharemyimage.com/smi/2025/10/21/Logo-Lavie-Saigon.jpeg',
    title: 'La Vie Saigon',
    slogan: 'Life, Bread & Tea',
    facebookLink: 'https://facebook.com',
    instagramLink: 'https://instagram.com',
    mapsLink: 'https://maps.google.com',
    copyright: '© 2025 La Vie Saigon. All rights reserved.'
  }
};

export const ThemeProvider = ({ children }) => {
  const [themeConfig, setThemeConfig] = useState(defaultThemeConfig);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    loadThemeConfig();
  }, []);

  const loadThemeConfig = async () => {
    try {
      const { data, error } = await supabase
        .from('theme_config')
        .select('*');

      if (error && error.code !== 'PGRST116') {
        console.error('Error loading theme config:', error);
        // Use default config on error
        setThemeConfig(defaultThemeConfig);
        setIsLoading(false);
        return;
      }

      if (data && data.length > 0) {
        const config = {};
        data.forEach(item => {
          config[item.section] = item.config;
        });
        setThemeConfig({ ...defaultThemeConfig, ...config });
      } else {
        // Use default if no data
        setThemeConfig(defaultThemeConfig);
      }
    } catch (error) {
      console.error('Error loading theme config:', error);
      setThemeConfig(defaultThemeConfig);
    } finally {
      setIsLoading(false);
    }
  };

  const updateThemeConfig = async (newConfig) => {
    try {
      // Update each section separately
      for (const [section, config] of Object.entries(newConfig)) {
        const { error } = await supabase
          .from('theme_config')
          .upsert(
            {
              section,
              config,
              updated_at: new Date().toISOString()
            },
            { onConflict: 'section' }
          );

        if (error) throw error;
      }

      setThemeConfig(newConfig);
    } catch (error) {
      console.error('Error updating theme config:', error);
    }
  };

  const resetToDefault = async () => {
    try {
      // Delete all existing theme config
      const { error: deleteError } = await supabase
        .from('theme_config')
        .delete()
        .neq('id', '00000000-0000-0000-0000-000000000000'); // Delete all

      if (deleteError) throw deleteError;

      // Insert default config
      const configEntries = Object.entries(defaultThemeConfig).map(([section, config]) => ({
        section,
        config
      }));

      const { error: insertError } = await supabase
        .from('theme_config')
        .insert(configEntries);

      if (insertError) throw insertError;

      setThemeConfig(defaultThemeConfig);
    } catch (error) {
      console.error('Error resetting theme config:', error);
    }
  };

  // Don't render children until theme is loaded
  if (isLoading) {
    return (
      <div className="min-h-screen bg-cream flex items-center justify-center">
        <div className="text-center">
          <img
            src="https://cdn1.sharemyimage.com/smi/2025/10/21/Logo-Lavie-Saigon.jpeg"
            alt="La Vie Saigon"
            className="h-16 w-16 mx-auto mb-4 rounded-full animate-pulse"
          />
          <h1 className="text-2xl font-playfair text-warm-brown mb-2">La Vie Saigon</h1>
          <p className="text-warm-brown/70">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <ThemeContext.Provider value={{
      themeConfig,
      updateThemeConfig,
      resetToDefault,
      isLoading
    }}>
      {children}
    </ThemeContext.Provider>
  );
};